import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import KanbanColumnWrapperView from "./KanbanColumnWrapperView";
import { Box } from "@/components/atoms/Box";
import { KanbanColumnProps } from "@/features/player-kanban/types/kanbanColumnWrapperTypes";

/**
 * Container component for a sortable kanban column with drag-and-drop functionality.
 * @param props - The kanban column props
 * @param props.columnId - Unique identifier for the column used in sorting and drag operations
 * @param props.title - The display title shown in the column header
 * @param props.count - The number of players currently in this column
 * @param props.players - Array of player objects to display in the column
 * @param props.isEmpty - Whether the column has no players and should show empty state
 * @param props.emptyText - Custom text to display when the column is empty
 * @param props.boardId - Parent board identifier for data scoping
 * @param props.boardRefreshTrigger - Counter that triggers board refresh when incremented
 * @param props.isGloballyDragging - Whether any item is currently being dragged globally
 * @param props.canEdit - Whether the user has permission to edit the column title
 * @param props.canDelete - Whether the user has permission to delete the column
 * @param props.onTitleChange - Callback invoked when the column title is changed
 * @param props.onPlayerDrop - Callback invoked when a player is dropped into this column
 * @param props.onPlayerClick - Callback invoked when a player card is clicked
 * @param props.onGlobalDragStart - Callback invoked when any drag operation starts
 * @param props.onGlobalDragEnd - Callback invoked when any drag operation ends
 * @param props.onAutoScroll - Callback invoked to trigger horizontal auto-scrolling during drag
 * @param props.onTagAdded - Callback invoked when a tag is added to a player
 * @param props.getPlayerTags - Function to retrieve tags for a specific player
 * @param props.onBoardRefresh - Callback invoked to refresh the entire board
 * @param props.onDelete - Callback invoked when the column is deleted
 * @remarks Uses Box component instead of HTML div for cross-platform compatibility with React Native.
 * Box component is fully compatible with @dnd-kit/sortable's setNodeRef and drag-and-drop operations.
 */
const KanbanColumnWrapperContainer = (props: KanbanColumnProps) => {
  const { columnId } = props;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: columnId,
      data: {
        type: "column",
        columnId,
      },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    // eslint-disable-next-line no-restricted-syntax -- dnd-kit sortable transform/transition (runtime web CSS); no closed token
    <Box ref={setNodeRef} UNSAFE_style={style} {...attributes} {...listeners}>
      <KanbanColumnWrapperView {...props} />
    </Box>
  );
};

export default KanbanColumnWrapperContainer;
